import {
  Breadcrumb,
  Modal,
  ProgressIndicator,
  Tag,
  TabsNavigation,
  Tab
} from '@/components/atoms';
import {
  PageHeader,
  SaveCancelFooter,
  SectionTitle
} from '@/components/molecules';
import PageContainer from '@/components/organisms/PageContainer';
import {ProductGtinAssignmentTable} from '@/components/molecules/ProductGtinAssignmentTable';
import {SalesTargetProducts} from '@/containers';
import {useCategoryList} from '@/hooks/useCategoryList';
import {useSalesTargets} from '@/hooks/useSalesTargets';
import {useAppDispatch, useAppSelector} from '@/store';

import {
  addExcludedProduct,
  addIncludedProduct,
  resetSalesTarget,
  setSalesTargetFormData,
  setSalesTargetSelectedCategory,
  setIgnoreSkuList,
  setSelectedRows,
  setPageActiveTab,
  addPreRequisiteParentTab,
  addPreRequisiteChildTab,
  setSelectedType
} from '@/store/slices/salesTargetSlice';
import {TAG_COLOR} from '@/utils/constants/colors';
import {Constants} from '@/utils/constants/commonConstants';
import {MODAL_TYPE} from '@/utils/constants/globalStyle';
import {ROUTES} from '@/utils/constants/routesConstants';
import {useCallback, useEffect, useState} from 'react';
import {TARGET_TYPES} from '@/utils/constants/saleTargetConstants';
import {isProductGroupQuantityType} from '@/utils/productGroupQuantity';
import {buildSalesTargetDraftPayload} from '@/pages/SalesTargetUtils';
import {useLocation, useNavigate} from 'react-router-dom';
import {chooseByCondition} from '@/helpers/shadcn/utils';
import {logger} from '@/helpers/logger';

// Lifted cellDataMapperObj
const cellDataMapperObj = {
  productName: ({row: {original} = {}}) => {
    return <div>{original?.productName}</div>;
  },
  productId: ({row: {original} = {}}) => {
    return <div>{original?.productId}</div>;
  },
  displayName: ({row: {original} = {}}) => {
    return <div>{original?.displayName?.en}</div>;
  },
  displaySize: ({row: {original} = {}}) => {
    return <div>{original?.displaySize?.en}</div>;
  },
  displayCategory: ({row: {original} = {}}) => {
    return <div>{original?.displayCategory?.en}</div>;
  },
  brand: ({row: {original} = {}}) => {
    return <div>{original?.brand?.en}</div>;
  },
  subcategory: ({row: {original} = {}}) => {
    return <div>{original?.subcategory?.en}</div>;
  }
};

const AddSalesTargetProduct = () => {
  const location = useLocation();
  const isEdit = location.state?.isEdit ?? false;
  const disableDraft = location.state?.disableDraft ?? false;
  const salesTargetIdFromState = location.state?.salesTargetId;

  const TABS = {PARENT: 'parent', CHILD: 'child'};

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const salesTarget = useAppSelector((state) => state.salesTarget);

  const getNavigationState = () => ({
    isEdit,
    disableDraft,
    editPublishedBeforeStart: disableDraft,
    salesTargetId: salesTargetIdFromState || salesTarget?.id
  });

  const isPrerequisiteTarget =
    salesTarget.targetType === TARGET_TYPES.MonthlyTargetPrerequistite ||
    salesTarget.targetType === TARGET_TYPES.ProductTargetPrerequisite;
  const isProductGroupQuantity = isProductGroupQuantityType(
    salesTarget?.targetType
  );
  const {handleDraft: handleDraftFromHook} = useSalesTargets(false);
  const [tab, setTab] = useState(TABS.PARENT);
  const {categoryListData} = useCategoryList();
  const selectedCategory = useAppSelector(
    (state) => state.salesTarget.selectedCategory
  );
  const parentSelectedCategory = useAppSelector(
    (state) => state.salesTarget.tabWiseData.parent.selectedCategory
  );
  const childSelectedCategory = useAppSelector(
    (state) => state.salesTarget.tabWiseData.child.selectedCategory
  );

  const handleIgnoreSkuChange = (val) => {
    dispatch(setIgnoreSkuList({activeTab: tab, ignoreSkuList: val}));
    if (tab === TABS.PARENT) {
      setParentIgnoreSku(val);
    }
    if (tab == TABS.CHILD) {
      setChildIgnoreSku(val);
    }
  };

  // const parentExcludedProducts = useAppSelector(state => state.salesTarget.parent.excludedProducts)
  // const parentIncludedProducts = useAppSelector(state => state.salesTarget.child.includedProducts)
  const ProductListColConfig = [
    {
      name_of_db_column: 'productName',
      label_of_column: 'Name',
      order_of_column: 1,
      columnFilterPlaceholder: 'Name',
      initialSize: 140
    },
    {
      name_of_db_column: 'productId',
      label_of_column: 'Product ID',
      order_of_column: 2,
      columnFilterPlaceholder: 'Product ID',
      initialSize: 130
    },
    {
      name_of_db_column: 'status',
      label_of_column: 'Status',
      order_of_column: 2,
      columnFilterPlaceholder: 'Status',
      initialSize: 110
    },
    {
      name_of_db_column: 'size',
      label_of_column: 'Size',
      order_of_column: 3,
      columnFilterPlaceholder: 'Size',
      initialSize: 100
    },
    {
      name_of_db_column: 'category_en',
      label_of_column: 'Category',
      order_of_column: 4,
      columnFilterPlaceholder: 'Category',
      initialSize: 130
    },
    {
      name_of_db_column: 'brand_en',
      label_of_column: 'Brand',
      order_of_column: 5,
      columnFilterPlaceholder: 'Brand',
      initialSize: 120
    },
    {
      name_of_db_column: 'includeExclude',
      label_of_column: 'Include/Exclude',
      order_of_column: 6,
      columnFilterPlaceholder: 'Include/Exclude',
      hideSortIcon: true,
      filterable: false,
      initialSize: 110
    }
  ];
  const [localToast, setLocalToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [isCancel, setIsCancel] = useState(false);
  const salesTargetData = useAppSelector((state) => state.salesTarget);
  const getSelectedType = (prerequisiteType) =>
    prerequisiteType === Constants.CategoryTable
      ? Constants.CategoryProducts
      : Constants.UploadedProducts;

  const childPrerequisiteType = Object.keys(
    salesTargetData?.preRequisiteChildTab
  ).find((key) => salesTargetData?.preRequisiteChildTab[key]);
  const parentPrerequisiteType = Object.keys(
    salesTargetData?.preRequisiteParentTab
  ).find((key) => salesTargetData?.preRequisiteParentTab[key]);

  // === Tab-specific states ===
  const [parentIgnoreSku, setParentIgnoreSku] = useState(
    salesTarget?.tabWiseData?.parent?.ignoreSkuList || false
  );
  const [childIgnoreSku, setChildIgnoreSku] = useState(
    salesTarget?.tabWiseData?.child?.ignoreSkuList || false
  );

  const [parentSelectedType, setParentSelectedType] = useState(
    parentPrerequisiteType
      ? getSelectedType(parentPrerequisiteType)
      : Constants.CategoryProducts
  );

  const [childSelectedType, setChildSelectedType] = useState(
    childPrerequisiteType
      ? getSelectedType(childPrerequisiteType)
      : Constants.CategoryProducts
  );

  const [parentUploadComplete, setParentUploadComplete] = useState(false);
  const [childUploadComplete, setChildUploadComplete] = useState(false);

  const [parentFormData, setParentFormData] = useState({});
  const [childFormData, setChildFormData] = useState({});

  const [parentUploadError, setParentUploadError] = useState(false);
  const [childUploadError, setChildUploadError] = useState(false);

  const [parentLocalProductData, setParentLocalProductData] = useState([]);
  const [childLocalProductData, setChildLocalProductData] = useState([]);
  const [parentRowSelection, setParentRowSelection] = useState(
    salesTarget?.tabWiseData?.parent?.selectedRows || []
  );
  const [childRowSelection, setChildRowSelection] = useState(
    salesTarget?.tabWiseData?.child?.selectedRows || []
  );

  const [selectedCategoryInitialState, setSelectedCategoryInitialState] =
    useState([]);
  const [disableNext, setDisableNext] = useState(true);
  const handleSetSelectedCategory = (newCategories) => {
    // Prevent redundant dispatch calls
    if (
      JSON.stringify(newCategories) !==
      JSON.stringify(salesTarget?.tabWiseData?.[tab]?.selectedCategory || [])
    ) {
      const catIds =
        categoryListData?.map((cat) => cat?.categoryId).filter((id) => id) ||
        [];
      const newCatIds =
        newCategories?.map((cat) => cat.id).filter((id) => id) || [];
      dispatch(
        setSalesTargetSelectedCategory({
          activeTab: tab,
          payload: newCategories,
          excludedCategories: catIds.filter(
            (catId) => !newCatIds.includes(catId)
          )
        })
      );
    }
  };

  const handleRowSelection = (rows) => {
    const ids = rows.map((row) => row.productId);
    dispatch(setSelectedRows({activeTab: tab, selectedRows: ids}));
    if (tab === TABS.PARENT) {
      setParentRowSelection(ids);
    } else {
      setChildRowSelection(ids);
    }
  };

  const handleRowIncludeExclude = (productId, isExcluded) => {
    if (isExcluded) {
      dispatch(addExcludedProduct({activeTab: tab, productId}));
    } else {
      dispatch(addIncludedProduct({activeTab: tab, productId}));
    }
  };

  const handleBulkAction = async (
    isExcluded,
    fetchAllProducts,
    searchState = [],
    isSelectAll = false
  ) => {
    try {
      if (
        [
          TARGET_TYPES.MainTarget,
          TARGET_TYPES.ProductTarget,
          TARGET_TYPES.MonthlyTargetLayered,
          TARGET_TYPES.ProductTargetLayered
        ].indexOf(salesTarget?.targetType) >= 0
      ) {
        dispatch(
          setPageActiveTab({activeTab: 'parent', pageTitle: 'Products'})
        );
      }

      if (isSelectAll && fetchAllProducts) {
        try {
          const allProducts = await fetchAllProducts(searchState);
          if (allProducts && allProducts.length > 0) {
            // Process all products for include/exclude (cumulative - no reset)
            allProducts.forEach((product) => {
              if (product?.productId) {
                handleRowIncludeExclude(product.productId, isExcluded);
              }
            });
          }
        } catch (processError) {
          logger.error('[handleBulkAction] Error in processing:', processError);
        }
      } else {
        // Process only the selected rows (cumulative - no reset)
        const selection =
          tab === TABS.PARENT ? parentRowSelection : childRowSelection;
        const validSelections =
          selection?.filter((id) => id && id !== 'select-all') || [];
        if (validSelections.length > 0) {
          validSelections.forEach((productId) => {
            handleRowIncludeExclude(productId, isExcluded);
          });
        }
      }
    } catch (error) {
      logger.error('[handleBulkAction] Unhandled error:', error);
    }
  };

  const getCategoryItemFromId = (categoryListData, selectedCategory) => {
    return (
      selectedCategory
        ?.map((catId) => {
          const match = categoryListData.find(
            (item) =>
              item?.categoryId === catId ||
              item?.categoryId === catId?.categoryId ||
              item?.categoryId === catId?.id
          );
          if (match?.categoryId && match?.category) {
            return {
              ...match,
              id: match.categoryId,
              displayText: match.category
            };
          }
          return null; // skip if not valid
        })
        ?.filter(Boolean) || []
    ); // remove null values
  };

  useEffect(() => {
    const {targetType, tabWiseData} = salesTarget;

    // Guard: ensure both categoryListData AND targetType are available
    if (!categoryListData.length || !targetType) return;

    dispatch(setPageActiveTab({pageTitle: 'Products', activeTab: TABS.PARENT}));

    const allCategories = categoryListData.map((item) => ({
      ...item,
      id: item.categoryId,
      displayText: item.category
    }));

    if (!isEdit) {
      // Apply auto-select only to specific target types and for parent tab
      const shouldAutoSelect = [
        TARGET_TYPES.MainTarget,
        TARGET_TYPES.MonthlyTargetLayered,
        TARGET_TYPES.MonthlyTargetPrerequistite
      ].includes(targetType);

      if (shouldAutoSelect) {
        // Always select for parent
        setSelectedCategoryInitialState(allCategories);

        dispatch(
          setSalesTargetSelectedCategory({
            activeTab: TABS.PARENT,
            payload: allCategories
          })
        );

        // Only skip child selection for MonthlyPrerequisite type
        if (targetType !== TARGET_TYPES.MonthlyTargetPrerequistite) {
          dispatch(
            setSalesTargetSelectedCategory({
              activeTab: TABS.CHILD,
              payload: allCategories
            })
          );
        }
      }
    } else {
      // Always use previously selected categories during edit
      setSelectedCategoryInitialState([]);

      dispatch(
        setSalesTargetSelectedCategory({
          activeTab: TABS.PARENT,
          payload: getCategoryItemFromId(
            categoryListData,
            tabWiseData[TABS.PARENT]?.selectedCategory
          )
        })
      );
      dispatch(
        setSalesTargetSelectedCategory({
          activeTab: TABS.CHILD,
          payload: getCategoryItemFromId(
            categoryListData,
            tabWiseData[TABS.CHILD]?.selectedCategory
          )
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    categoryListData.length,
    salesTarget.targetType,
    isEdit,
    TABS.PARENT
  ]);

  const checkIsAllCategories = (selectedCategory) =>
    selectedCategory &&
    categoryListData &&
    categoryListData.length === selectedCategory.length;

  const getNonEmptyArray = (primary, fallback) =>
    Array.isArray(primary) && primary.length > 0 ? primary : fallback || [];

  const getExcludedCategories = (parentRuleData, childRuleData) => {
    return {
      parentExcludedCategories: parentRuleData?.excludedCategories || [],
      childExcludedCategories: childRuleData?.excludedCategories || []
    };
  };
  const handleNextAction = async () => {
    if (isProductGroupQuantity) {
      if (disableDraft) {
        navigate(ROUTES.SALES_TARGET_REVIEW, {
          state: getNavigationState()
        });
        return;
      }
      navigate(ROUTES.SALES_TARGET_REVIEW);
      return;
    }

    const parentTabData = salesTarget.tabWiseData[TABS.PARENT];
    const childTabData = salesTarget.tabWiseData[TABS.CHILD];
    const parentRuleData = parentTabData?.ruleSet?.rules?.[0];
    const childRuleData = childTabData?.ruleSet?.rules?.[0];
    const {parentExcludedCategories, childExcludedCategories} =
      getExcludedCategories(parentRuleData, childRuleData);
    const parentIncludedProducts = getNonEmptyArray(
      parentTabData?.includedProducts,
      parentRuleData?.includedProductIds
    );
    const parentExcludedProducts = getNonEmptyArray(
      parentTabData?.excludedProducts,
      parentRuleData?.excludedProductIds
    );
    const childIncludedProducts = getNonEmptyArray(
      childTabData?.includedProducts,
      childRuleData?.includedProductIds
    );
    const childExcludedProducts = getNonEmptyArray(
      childTabData?.excludedProducts,
      childRuleData?.excludedProductIds
    );

    const productSelections = {
      parent: {
        included: parentIncludedProducts,
        excluded: parentExcludedProducts
      },
      child: {
        included: childIncludedProducts,
        excluded: childExcludedProducts
      },
      current: {
        tab: tab,
        included: salesTarget.tabWiseData[tab]?.includedProducts || [],
        excluded: salesTarget.tabWiseData[tab]?.excludedProducts || []
      },
      all: {
        included: [...parentIncludedProducts, ...childIncludedProducts],
        excluded: [...parentExcludedProducts, ...childExcludedProducts]
      }
    };
    const hasParentChildStructure = [
      TARGET_TYPES.MonthlyTargetPrerequistite,
      TARGET_TYPES.ProductTargetPrerequisite
    ].includes(salesTarget.targetType);
    const includeAllProductsByDefault = [
      TARGET_TYPES.MainTarget,
      TARGET_TYPES.MonthlyTargetLayered,
      TARGET_TYPES.MonthlyTargetPrerequistite
    ].includes(salesTarget.targetType);

    // Parent selected categories are the source of truth for includedCategories in the combined ruleSet
    const parentSelectedCategory =
      salesTarget.tabWiseData.parent?.selectedCategory || [];

    const ruleSet = {
      name: null,
      includeAllProductsByDefault: includeAllProductsByDefault,
      rules: [
        {
          // Use parent tab's selected categories so Review can display them correctly
          includedCategories:
            parentSelectedCategory?.map((cat) => cat.displayText) || [],
          includedProductIds: chooseByCondition(
            hasParentChildStructure,
            productSelections.all.included,
            productSelections.current.included
          ),
          excludedCategories: parentExcludedCategories,
          excludedProductIds: chooseByCondition(
            hasParentChildStructure,
            productSelections.all.excluded,
            productSelections.current.excluded
          )
        }
      ]
    };
    const parentRuleSet = {
      name: null,
      includeAllProductsByDefault: includeAllProductsByDefault,
      rules: [
        {
          includedCategories:
            parentSelectedCategory.map((cat) => cat.displayText) || [],
          includedProductIds: parentIncludedProducts,
          excludedCategories: parentExcludedCategories,
          excludedProductIds: parentExcludedProducts
        }
      ]
    };
    // Build childRuleSet for child tabWiseData
    const childSelectedCategory =
      salesTarget.tabWiseData.child?.selectedCategory || [];
    const childRuleSet = {
      name: null,
      includeAllProductsByDefault: false,
      rules: [
        {
          includedCategories: childSelectedCategory.map((cat) => cat.id) || [],
          includedProductIds: childIncludedProducts,
          excludedCategories: childExcludedCategories,
          excludedProductIds: childExcludedProducts
        }
      ]
    };

    dispatch(
      setSalesTargetFormData({
        ...salesTarget.formData,
        selectedSalesTargetProduct:
          tab === TABS.PARENT ? parentLocalProductData : childLocalProductData,
        ruleSet,
        isAllCategories: checkIsAllCategories(parentSelectedCategory),
        isAllCategoriesChild: checkIsAllCategories(childSelectedCategory),
        selectedType:
          tab === TABS.PARENT ? parentSelectedType : childSelectedType,
        productSelections,
        tabWiseData: {
          ...salesTarget.tabWiseData,
          activeTab: TABS.PARENT,
          parent: {
            ...salesTarget.tabWiseData.parent,
            includedProducts: parentIncludedProducts,
            excludedProducts: parentExcludedProducts,
            ruleSet: {
              ...parentRuleSet,
              rules: [
                {
                  ...parentRuleSet.rules[0],
                  includedProductIds: parentIncludedProducts,
                  excludedProductIds: parentExcludedProducts
                }
              ]
            }
          },
          child: {
            ...salesTarget.tabWiseData.child,
            includedProducts: childIncludedProducts,
            excludedProducts: childExcludedProducts,
            ruleSet: {
              ...childRuleSet,
              rules: [
                {
                  ...childRuleSet.rules[0],
                  includedProductIds: childIncludedProducts,
                  excludedProductIds: childExcludedProducts
                }
              ]
            }
          }
        }
      })
    );

    if (salesTarget.tabWiseData.parent.mediaFile.length == 1) {
      dispatch(
        addPreRequisiteParentTab({
          categoryTable: false,
          uploadFile: true
        })
      );
    } else {
      dispatch(
        addPreRequisiteParentTab({
          categoryTable: true,
          uploadFile: false
        })
      );
    }
    if (salesTarget.tabWiseData.child.mediaFile.length == 1) {
      dispatch(
        addPreRequisiteChildTab({
          categoryTable: false,
          uploadFile: true
        })
      );
    } else {
      dispatch(
        addPreRequisiteChildTab({
          categoryTable: true,
          uploadFile: false
        })
      );
    }
    if (disableDraft) {
      navigate(ROUTES.SALES_TARGET_REVIEW, {
        state: {
          isEdit,
          disableDraft,
          editPublishedBeforeStart: disableDraft,
          salesTargetId: salesTargetIdFromState || salesTarget?.id
        }
      });
      return;
    }

    navigate(ROUTES.SALES_TARGET_REVIEW);
  };

  const handleDraft = async () => {
    const {salesTargetForDraft, selectedTypeForDraft} =
      buildSalesTargetDraftPayload({
        salesTarget,
        isPrerequisiteTarget,
        fallbackSelectedType: parentSelectedType
      });

    // Keep Redux selectedType aligned for UI purposes
    dispatch(setSelectedType(selectedTypeForDraft));

    const response = await handleDraftFromHook(
      salesTargetForDraft,
      salesTargetForDraft,
      isPrerequisiteTarget,
      'products',
      {categoryListData}
    );
    if (response.success) {
      navigate(ROUTES.SALES_TARGETS);
    }
  };

  const handleCancel = () => setIsCancel(!isCancel);

  const onClickPrimaryButton = () => {
    dispatch(resetSalesTarget());
    navigate(ROUTES.SALES_TARGETS);
  };

  const onClickSecondaryButton = () => setIsCancel(!isCancel);

  const isProductIncludedDefault = useCallback(
    (product) =>
      selectedCategory?.some((cat) => cat.id === product?.displayCategoryId),
    [selectedCategory]
  );

  const handleStepClick = (step) => {
    // 1: Setup, 2: Display Content, 3: Products, 4: Review
    if (step === 1)
      navigate(ROUTES.SALES_TARGET_ADD, {
        state: getNavigationState()
      });
    if (step === 2)
      navigate(ROUTES.SALES_TARGET_DISPLAY_CONTENT, {
        state: getNavigationState()
      });
    // Don't allow navigating to current or future steps from here
  };

  const tabConfig = {
    [TABS.PARENT]: {
      ignoreSkuList: parentIgnoreSku,
      selectedType: parentSelectedType,
      setSelectedType: setParentSelectedType,
      uploadComplete: parentUploadComplete,
      setUploadComplete: setParentUploadComplete,
      formData: parentFormData,
      setFormData: setParentFormData,
      uploadError: parentUploadError,
      setUploadError: setParentUploadError,
      getRowSelection: () => parentRowSelection,
      setProductData: setParentLocalProductData,
      getProductData: () => parentLocalProductData,
      localToast: localToast,
      selectedCategory: parentSelectedCategory,
      handleIgnoreSkuChange: handleIgnoreSkuChange,
      // selectedCategory: ()=> setSelectedCategory,
      selectedCategoryInitialState: selectedCategoryInitialState,
      setLocalToast: setLocalToast
    },
    [TABS.CHILD]: {
      ignoreSkuList: childIgnoreSku,
      selectedType: childSelectedType,
      setSelectedType: setChildSelectedType,
      uploadComplete: childUploadComplete,
      handleIgnoreSkuChange: handleIgnoreSkuChange,
      setUploadComplete: setChildUploadComplete,
      formData: childFormData,
      setFormData: setChildFormData,
      uploadError: childUploadError,
      setUploadError: setChildUploadError,
      getRowSelection: () => childRowSelection,
      setProductData: setChildLocalProductData,
      selectedCategory: childSelectedCategory,
      getProductData: () => childLocalProductData,
      selectedCategoryInitialState: []
    }
  };
  const isValidParentForm = useCallback(() => {
    if (salesTarget.tabWiseData?.parent?.selectedCategory?.length > 0) {
      return true;
    }
    return (
      salesTarget.tabWiseData?.parent?.includedProducts?.length > 0 ||
      salesTarget.tabWiseData?.parent?.excludedProducts?.length > 0 ||
      salesTarget.tabWiseData?.parent?.ruleSet?.rules[0]?.includedProductIds
        ?.length > 0
    );
  }, [salesTarget.tabWiseData?.parent]);
  const isValidChildForm = useCallback(() => {
    // skip for the TARGET_TYPES which is not pre requisite
    if (
      [
        TARGET_TYPES.MainTarget,
        TARGET_TYPES.ProductTarget,
        TARGET_TYPES.MonthlyTargetLayered,
        TARGET_TYPES.ProductTargetLayered
      ].indexOf(salesTarget?.targetType) >= 0
    ) {
      return true;
    }
    if (salesTarget.tabWiseData?.child?.selectedCategory?.length > 0) {
      return true;
    }
    return (
      salesTarget.tabWiseData?.child?.includedProducts?.length > 0 ||
      salesTarget.tabWiseData?.child?.excludedProducts?.length > 0 ||
      salesTarget.tabWiseData?.child?.ruleSet?.rules[0]?.includedProductIds
        ?.length > 0
    );
  }, [salesTarget?.targetType, salesTarget.tabWiseData?.child]);
  useEffect(() => {
    if (isProductGroupQuantity) {
      setDisableNext(false);
      return;
    }
    const v = isValidChildForm() && isValidParentForm();
    setDisableNext(!v);
  }, [
    isProductGroupQuantity,
    salesTarget,
    selectedCategory,
    isValidChildForm,
    isValidParentForm,
    parentIgnoreSku,
    childIgnoreSku,
    parentSelectedType,
    childSelectedType,
    parentLocalProductData,
    childLocalProductData,
    parentUploadError,
    childUploadError,
    parentUploadComplete,
    childUploadComplete,
    disableNext,
    parentUploadError,
    setParentUploadError
  ]);
  const handleTabSelection = (val, e) => {
    if (val === TABS.CHILD && !isValidParentForm()) {
      setLocalToast(true);
      setToastMessage(`Product(s) must be selected in the ${TABS.PARENT} tab.`);
      e.stopPropagation();
      return;
    }
    if (val == TABS.PARENT && isValidParentForm() && !isValidChildForm()) {
      setToastMessage(`Product(s) must be selected in the ${TABS.CHILD} tab.`);
      setLocalToast(true);
    }
    setTab(val);
    dispatch(setPageActiveTab({pageTitle: 'Products', activeTab: val}));
  };
  return (
    <>
      <Breadcrumb
        uiSchema={{
          isDark: true,
          className: 'mb-5',
          breadcrumbs: [
            {href: ROUTES.HOME, label: ''},
            {href: ROUTES.SALES_TARGETS, label: 'Loyalty Campaigns'},
            {href: ROUTES.SALES_TARGETS, label: 'Add Sales Target'}
          ]
        }}
        id={'astp_bradcrumb'}
      />

      <PageHeader title='Add Sales Target' />

      <ProgressIndicator
        currentStep={3}
        completed={false}
        steps={4}
        titles={[
          {title: 'Setup'},
          {title: 'Display Content'},
          {title: 'Products'},
          {title: 'Review'}
        ]}
        className='mb-6'
        onStepClick={handleStepClick}
      />
      {isProductGroupQuantity ? (
        <PageContainer data-testid='product-assignment'>
          <SectionTitle label='Product Assignment' className='w-full pb-3' />
          <ProductGtinAssignmentTable
            productGtins={salesTarget?.productGtins}
          />
        </PageContainer>
      ) : (
        <>
          {[
            TARGET_TYPES.MonthlyTargetPrerequistite,
            TARGET_TYPES.ProductTargetPrerequisite
          ].indexOf(salesTarget?.targetType) >= 0 && (
            <TabsNavigation>
              <Tab
                label='Parent'
                isActive={tab === TABS.PARENT}
                onClick={(e) => handleTabSelection(TABS.PARENT, e)}
              />
              <Tab
                label='Child'
                isActive={tab === TABS.CHILD}
                onClick={(e) => handleTabSelection(TABS.CHILD, e)}
                onClickCapture={(e) => {
                  return handleTabSelection(TABS.CHILD, e);
                }}
              />
            </TabsNavigation>
          )}

          <SalesTargetProducts
            key={tab}
            name={tab}
            setShowToast={setLocalToast}
            showToast={localToast}
            toastMessage={toastMessage}
            ignoreSkuList={tabConfig[tab].ignoreSkuList}
            handleIgnoreSkuChange={tabConfig[tab].handleIgnoreSkuChange}
            selectedType={tabConfig[tab].selectedType}
            setSelectedType={tabConfig[tab].setSelectedType}
            uploadComplete={tabConfig[tab].uploadComplete}
            setUploadComplete={tabConfig[tab].setUploadComplete}
            formData={tabConfig[tab].formData}
            setFormData={tabConfig[tab].setFormData}
            uploadError={tabConfig[tab].uploadError}
            setUploadError={tabConfig[tab].setUploadError}
            cellDataMapperObj={{
              ...cellDataMapperObj,
              includeExclude: ({row: {original} = {}}) => {
                // salesTarget.tabWiseData?.parent?.includedProducts?
                const isIncluded = salesTarget.tabWiseData[
                  tab
                ]?.includedProducts?.includes(original?.productId);
                return (
                  <Tag
                    text={isIncluded ? 'Exclude' : 'Include'}
                    color={isIncluded ? TAG_COLOR.RED_150 : TAG_COLOR.GREEN_150}
                    onClick={() =>
                      handleRowIncludeExclude(original?.productId, !isIncluded)
                    }
                  />
                );
              }
            }}
            ProductListColConfig={ProductListColConfig} // Assuming defined elsewhere
            rowSelection={tabConfig[tab].getRowSelection()}
            setRowSelection={handleRowSelection}
            handleBulkAction={handleBulkAction}
            categoryListData={categoryListData}
            selectedCategory={tabConfig[tab].selectedCategory}
            selectedCategoryInitialState={
              tabConfig[tab].selectedCategoryInitialState
            }
            setSelectedCategory={handleSetSelectedCategory}
            isProductIncludedDefault={isProductIncludedDefault}
            targetType={salesTarget?.targetType}
            isAllCategories={checkIsAllCategories(parentSelectedCategory)}
            isAllCategoriesChild={checkIsAllCategories(childSelectedCategory)}
            localProductData={tabConfig[tab].getProductData()}
            setLocalProductData={tabConfig[tab].setProductData}
            isEdit={isEdit}
          />
        </>
      )}
      {isCancel ? (
        <Modal
          type={MODAL_TYPE.FLUID}
          showModal={isCancel}
          onCloseModal={handleCancel}
          title='Are you sure you want to cancel?'
          text={
            'Your work will not be saved if you click Yes. To continue editing and go back to the page, click NO'
          }
          showPrimaryButton={true}
          primaryButtonText='Yes'
          onClickPrimaryButton={onClickPrimaryButton}
          showSecondaryButton={true}
          secondaryButtonText='No'
          onClickSecondaryButton={onClickSecondaryButton}
          footerProps={{
            size: 'medium',
            type: 'primary'
          }}
          isDark={true}
        />
      ) : null}
      <SaveCancelFooter
        // FormError={getFormError()}
        handleCancel={handleCancel}
        draftAction={handleDraft}
        nextAction={handleNextAction}
        FormError={disableNext}
        disableDraft={disableDraft}
      />
    </>
  );
};

export default AddSalesTargetProduct;
